import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import HomePage from "./client/pages/home";
import Navbar from "./components/Navbar";
import { LazySection } from "./components/common/LazySection";
import { ARTICLES_VISIBLE } from "./config/features";

const Footer = lazy(() => import("./components/Footer"));
const AppToaster = lazy(() =>
  import("./components/AppToaster").then((m) => ({ default: m.AppToaster }))
);

const Services = lazy(() => import("./client/pages/pay-services/Services"));
const Resources = lazy(() => import("./client/pages/resources/Resources"));
const AboutUs = lazy(() => import("./client/pages/aboutus/AboutUs"));
const CheckoutSuccess = lazy(() => import("./pages/checkout/CheckoutSuccess"));
const Terms = lazy(() => import("./client/pages/terms/Terms"));
const Privacy = lazy(() => import("./client/pages/terms/Privacy"));
const CareersPage = lazy(() => import("./client/pages/careers/Careers"));
const BookingPage = lazy(() => import("./client/pages/booking/BookingPage"));
const CartoonsPage = lazy(() => import("./client/pages/cartoons/CartoonsPage"));

const FortuneCookie = lazy(() =>
  import("./components/fortune-cookie/FortuneCookie").then(module => ({ default: module.FortuneCookie }))
);

const ArticleListPage = lazy(
  () => import("./client/pages/articles/ArticleListPage"),
);
const ArticlePage = lazy(
  () => import("./client/pages/articles/ArticlePage"),
);

/**
 * ScrollToTop - Automatically scrolls the viewport to the top of the page on route changes.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    // Start animations after component mounts
    const animationTimer = setTimeout(() => {
      setAnimationStarted(true);
    }, 100);

    return () => {
      clearTimeout(animationTimer);
    };
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <AppToaster />
      </Suspense>
      
      <div className="min-h-screen bg-white relative overflow-hidden">
        <div
          className={`bubble-container fixed inset-0 overflow-hidden transition-opacity duration-1000 z-0 pointer-events-none ${
            animationStarted ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="bubble bubble-global-1"></div>
          <div className="bubble bubble-global-2"></div>
          <div className="bubble bubble-global-3"></div>
          <div className="bubble bubble-global-4"></div>
          <div className="bubble bubble-global-5"></div>
        </div>

        {/* Original background image with reduced opacity */}
        <img
          src="/assets/home/hero/hero-bg-3.svg"
          fetchPriority="high"
          className={`fixed w-full h-screen object-cover transition-opacity duration-1000 z-0 pointer-events-none ${
            animationStarted ? "opacity-10" : "opacity-0"
          } scale-[180%] [transform:translateZ(0)]`}
          alt="Background"
        />

        <HelmetProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Navbar />
          {new Date().getDay() === 5 && (
            <Routes>
              <Route
                path="/"
                element={
                  <div className="fixed top-0 left-0 w-full z-[9999]">
                    <Suspense fallback={null}>
                      <FortuneCookie />
                    </Suspense>
                  </div>
                }
              />
            </Routes>
          )}
          <main className="relative z-10 flex-grow">
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center pt-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-cerulean/30 border-t-cerulean" />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<Services />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/school-referral" element={<Resources />} />
                <Route path="/terms-and-conditions" element={<Terms />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/privacy-policy" element={<Privacy />} />
                <Route path="/checkout-success" element={<CheckoutSuccess />} />
                 <Route path="/booking" element={<BookingPage />} />
                {ARTICLES_VISIBLE ? (
                  <>
                    <Route path="/articles" element={<ArticleListPage />} />
                    <Route path="/articles/:slug" element={<ArticlePage />} />
                  </>
                ) : (
                  <>
                    <Route path="/articles" element={<Navigate to="/" replace />} />
                    <Route path="/articles/:slug" element={<Navigate to="/" replace />} />
                  </>
                )}
                <Route path="/cartoons" element={<CartoonsPage />} />
              </Routes>
            </Suspense>
          </main>
            <LazySection minHeight="240px" rootMargin="500px 0px">
              <Suspense fallback={<div className="min-h-[200px]" />}>
                <Footer />
              </Suspense>
            </LazySection>
          </BrowserRouter>
        </HelmetProvider>
      </div>
    </>
  );
}

export default App;
